import React, { useMemo } from 'react';
import { UtilizationRing } from './NodeUtilizationBadge';
import { utilizationColor, formatQps } from '../../validation/traffic-types';
import useSimulationStore from '../../store/simulationStore';

/**
 * SVG overlay for the ReactFlow canvas showing traffic flow animation
 * This component renders on top of the flow canvas
 */
const SimulationOverlay = ({ nodes, edges, transform }) => {
  const { simulationState, currentStep, isAnimating, spofAnalysis } = useSimulationStore();

  // Create node position map
  const nodePositions = useMemo(() => {
    const map = {};
    nodes.forEach((node) => {
      map[node.id] = {
        x: node.position.x + (node.width || 150) / 2,
        y: node.position.y + (node.height || 60) / 2,
        width: node.width || 150,
        height: node.height || 60,
      };
    });
    return map;
  }, [nodes]);

  // Get current flows based on step
  const currentFlows = useMemo(() => {
    if (!simulationState?.flows) return [];

    // Filter flows up to current step
    return simulationState.flows.filter((flow) => flow.timestamp <= currentStep + 1);
  }, [simulationState, currentStep]);

  // Get SPOF status for nodes
  const spofStatus = useMemo(() => {
    if (!spofAnalysis) return {};
    const status = {};
    spofAnalysis.forEach((analysis) => {
      if (analysis.is_spof) {
        status[analysis.node_id] = analysis.failure_impact;
      }
    });
    return status;
  }, [spofAnalysis]);

  if (!simulationState) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* SVG for flow lines and particles */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
          transformOrigin: '0 0',
        }}
      >
        <defs>
          {/* Gradient for traffic flow */}
          <linearGradient id="readFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="writeFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="asyncFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.2" />
          </linearGradient>

          {/* Particle filter for glow effect */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Render flow particles */}
        {isAnimating &&
          currentFlows.map((flow, idx) => {
            const sourcePos = nodePositions[flow.source_node_id];
            const targetPos = nodePositions[flow.target_node_id];

            if (!sourcePos || !targetPos) return null;

            const color =
              flow.traffic_type === 'read'
                ? '#22c55e'
                : flow.traffic_type === 'write'
                ? '#3b82f6'
                : '#f97316';

            // Calculate particle size based on QPS
            const particleSize = Math.max(3, Math.min(8, flow.qps / 5000));

            return (
              <g key={`flow-${idx}`}>
                {/* Flow line */}
                <line
                  x1={sourcePos.x}
                  y1={sourcePos.y}
                  x2={targetPos.x}
                  y2={targetPos.y}
                  stroke={color}
                  strokeWidth={1}
                  strokeOpacity={0.3}
                  strokeDasharray="4 4"
                />

                {/* Animated particle */}
                <circle r={particleSize} fill={color} filter="url(#glow)">
                  <animateMotion
                    dur={`${1 / (flow.qps / 10000 + 0.5)}s`}
                    repeatCount="indefinite"
                    path={`M${sourcePos.x},${sourcePos.y} L${targetPos.x},${targetPos.y}`}
                  />
                </circle>

                {/* QPS label on flow */}
                <text
                  x={(sourcePos.x + targetPos.x) / 2}
                  y={(sourcePos.y + targetPos.y) / 2 - 10}
                  fill={color}
                  fontSize="10"
                  textAnchor="middle"
                  className="select-none"
                >
                  {formatQps(flow.qps)}
                </text>
              </g>
            );
          })}
      </svg>

      {/* Node utilization badges */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
          transformOrigin: '0 0',
        }}
      >
        {nodes.map((node) => {
          const utilization = simulationState.node_utilization?.[node.id] || 0;
          const qps = simulationState.node_qps?.[node.id] || 0;
          const isFailed = simulationState.failed_nodes?.includes(node.id);
          const isBottleneck = simulationState.bottlenecks?.some((b) => b.node_id === node.id);
          const spofImpact = spofStatus[node.id];

          // Skip nodes with no activity
          if (utilization === 0 && !isFailed && !spofImpact) return null;

          return (
            <div
              key={node.id}
              className="absolute"
              style={{
                left: node.position.x,
                top: node.position.y - 50,
                width: node.width || 150,
              }}
            >
              <div className="flex flex-col items-center">
                {/* SPOF warning */}
                {spofImpact && (
                  <div
                    className={`text-xs px-2 py-0.5 rounded mb-1 ${
                      spofImpact === 'complete'
                        ? 'bg-red-900/80 text-red-300'
                        : 'bg-yellow-900/80 text-yellow-300'
                    }`}
                  >
                    SPOF
                  </div>
                )}

                {/* Failed indicator */}
                {isFailed ? (
                  <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                    FAILED
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-slate-800/90 rounded-lg px-2 py-1 shadow-lg border border-slate-600">
                    <UtilizationRing utilization={utilization} size={24} strokeWidth={3} />
                    <div className="text-xs">
                      <div style={{ color: utilizationColor(utilization) }} className="font-bold">
                        {Math.round(utilization * 100)}%
                      </div>
                      <div className="text-slate-400">{formatQps(qps)}</div>
                    </div>
                    {isBottleneck && (
                      <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SimulationOverlay;
